// @ts-nocheck -- UI renderer landed during the TS migration; tighten once the
// trend-summary interaction contract settles.

import { escapeHtml } from "../core/html.js";
import { openAiUserFacingErrorMessage } from "./openai-error.js";

export function trendAiSummaryPayloadJson(payload) {
  return payload?.trendAiSummary?.summary_json
    ?? payload?.trendAiSummary?.summaryJson
    ?? payload?.summary_json
    ?? payload?.summaryJson
    ?? payload
    ?? null;
}

export function trendAiSummaryGeneratedLabel(payload) {
  const summary = trendAiSummaryPayloadJson(payload);
  const generated = summary?.generator?.generatedAt
    ?? payload?.trendAiSummary?.generated_at
    ?? payload?.generated_at
    ?? null;
  if (!generated) return "";
  const date = new Date(generated);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function stripMarkdownEmphasis(value) {
  return String(value ?? "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/`([^`]+)`/g, "$1");
}

function compactText(value, fallback = "") {
  const cleaned = stripMarkdownEmphasis(value);
  return cleaned.replace(/\s+/g, " ").trim() || fallback;
}

function paragraphTexts(value, fallback = "") {
  const cleaned = stripMarkdownEmphasis(value)
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
  const paragraphs = cleaned
    .split(/\n\s*\n+/)
    .map((part) => part.replace(/\s+/g, " ").trim())
    .filter(Boolean);
  if (paragraphs.length > 1) return paragraphs;
  if (paragraphs.length === 1) return splitLongParagraph(paragraphs[0]);
  const fallbackText = compactText(value, fallback);
  return fallbackText ? splitLongParagraph(fallbackText) : [];
}

function splitLongParagraph(text) {
  const cleaned = compactText(text);
  if (!cleaned) return [];
  const sentences = cleaned
    .split(/(?<=[.!?])\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (sentences.length < 3 || cleaned.length < 260) return [cleaned];
  if (sentences.length === 3) return sentences;
  const first = sentences[0];
  const second = sentences[1];
  const remaining = sentences.slice(2).join(" ");
  return [first, second, remaining].filter(Boolean);
}

function overviewParagraphsHtml(value) {
  const paragraphs = paragraphTexts(value);
  if (!paragraphs.length) return "";
  return `
    <div class="trend-ai-summary-overview">
      ${paragraphs.map((paragraph) => `<p class="trend-ai-summary-overview-paragraph">${escapeHtml(paragraph)}</p>`).join("")}
    </div>
  `;
}

function trendAiSummaryLoadingMarkup() {
  return `
    <div class="dashboard-loading-card ai-summary-loading-card trend-ai-summary-loading-card" role="status" aria-live="polite">
      <div class="dashboard-loading-instrument" aria-hidden="true">
        <div class="dashboard-loading-sweep"></div>
        <div class="dashboard-loading-needle"></div>
      </div>
      <div class="dashboard-loading-copy">
        <p class="eyebrow">AI Trend Summary</p>
        <h2>Generating AI summary</h2>
        <p>Going through the selected trend flights and writing the summary. This can take up to 2 minutes.</p>
      </div>
      <ol class="dashboard-loading-steps" aria-label="Trend AI summary generation progress">
        <li class="is-complete">Trend flights gathered</li>
        <li class="is-active">Writing the summary</li>
        <li>Almost ready</li>
      </ol>
    </div>
  `;
}

function shouldShowBoundary(overview, limitation) {
  const boundary = compactText(limitation);
  if (!boundary) return false;
  const overviewText = compactText(overview).toLowerCase();
  if (/diagnos/.test(overviewText) && /airworthiness/.test(overviewText)) return false;
  return true;
}

function compactSentence(value, fallback = "", maxLength = 150) {
  const text = compactText(value, fallback);
  if (text.length <= maxLength) return text;
  const sentence = text.split(/(?<=[.!?])\s+/)[0] ?? "";
  if (sentence && sentence.length <= maxLength) return sentence;
  return `${text.slice(0, Math.max(0, maxLength - 3)).trim()}...`;
}

function textTone(value, fallback = "neutral") {
  const text = String(value ?? "");
  const positiveCue = /balanced|clean|consistent|efficient|excellent|good|healthy|improved|improving|normal|nominal|positive|stable|steady|well/i;
  const negatedConcern = /\b(?:no|without)\s+(?:[\w-]+\s+){0,4}(?:alerts?|concerns?|flags?|flagged|heating\s+issues?|issues?|problems?)\b|(?:alerts?|concerns?|issues?|problems?)\s+(?:were\s+)?(?:not|absent)/i;
  if (negatedConcern.test(text) && positiveCue.test(text)) return "positive";
  if (/alert|attention|flagged|investigate|issue|needed|poor|rapid|\bwatch (status|signal|item)\b|worse|worsening/i.test(text)) return "watch";
  if (positiveCue.test(text)) return "positive";
  return fallback;
}

function evidenceItems(summary, limit = 6) {
  const stableEvidence = (Array.isArray(summary?.supportingEvidence) ? summary.supportingEvidence : [])
    .map((item) => ({
      label: compactText(item?.label, "Evidence"),
      value: compactText(item?.value),
      detail: compactText(item?.detail),
      tone: textTone(`${item?.label ?? ""} ${item?.value ?? ""} ${item?.detail ?? ""}`),
    }))
    .filter((item) => item.value || item.detail)
    .slice(0, limit);
  if (stableEvidence.length) return stableEvidence;
  return (Array.isArray(summary?.mostImportantChanges) ? summary.mostImportantChanges : [])
    .map((item) => ({
      label: compactText(item?.title, "Trend"),
      value: compactText(item?.direction),
      detail: compactText(item?.text),
      tone: item?.direction === "worsening" ? "watch" : textTone(`${item?.direction ?? ""} ${item?.text ?? ""}`, "positive"),
    }))
    .filter((item) => item.label || item.detail)
    .slice(0, limit);
}

function changeItems(summary) {
  return (Array.isArray(summary?.mostImportantChanges) ? summary.mostImportantChanges : [])
    .map((item) => ({
      label: compactText(item?.title, "Trend"),
      value: compactText(item?.direction),
      detail: compactText(item?.text),
      tone: item?.direction === "worsening" ? "watch" : textTone(`${item?.direction ?? ""} ${item?.title ?? ""} ${item?.text ?? ""}`, "positive"),
    }))
    .filter((item) => item.label || item.detail)
    .slice(0, 6);
}

function focusItems(summary) {
  return (Array.isArray(summary?.suggestedReviewFocus) ? summary.suggestedReviewFocus : [])
    .map((item) => ({
      label: compactText(item?.title, "Next comparison"),
      detail: compactText(item?.text),
      tone: "focus",
    }))
    .filter((item) => item.label || item.detail)
    .slice(0, 2);
}

function uniqueItems(items) {
  const seen = new Set();
  return items.filter((item) => {
    const key = compactText(item?.label).toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const deterministicGoodSignalPriority = Object.freeze([
  "valveScore",
  "oilSystemScore",
  "coolingRateScore",
  "coolingScore",
  "chtScore",
  "egtScore",
  "electricalScore",
  "chargingScore",
  "sensorScore",
  "turboScore",
]);

function deterministicSignalStatus(signal) {
  const flaggedCount = Number(signal?.flaggedCount ?? 0);
  const latestScore = Number.isFinite(signal?.latestScore) ? signal.latestScore : NaN;
  if (!flaggedCount && Number.isFinite(latestScore) && latestScore >= 88) {
    return { label: "Stable", className: "green" };
  }
  return signal?.status ?? null;
}

function deterministicSignalRank(signal) {
  const index = deterministicGoodSignalPriority.indexOf(signal?.key);
  return index >= 0 ? index : deterministicGoodSignalPriority.length;
}

function deterministicSignalDirectionText(signal) {
  if (signal?.direction === "worse") return "worse recently";
  if (signal?.direction === "better") return "improving recently";
  return "holding steady, not worsening";
}

function deterministicSignalDetail(signal, tone) {
  const label = compactText(signal?.label ?? signal?.title, "this area");
  const affected = compactText(signal?.affected);
  const metricRows = Number(signal?.metricRows ?? 0);
  const flaggedCount = Number(signal?.flaggedCount ?? 0);
  const recentFlagged = Number(signal?.recentFlagged ?? 0);
  const recentCount = Number(signal?.recentCount ?? 0);
  const selectedCount = metricRows || recentCount;
  const selectedFlagged = flaggedCount || recentFlagged;
  const latestScore = Number.isFinite(signal?.latestScore) ? signal.latestScore : NaN;
  const pieces = [];
  if (tone === "positive") {
    if (metricRows) {
      pieces.push(`Clear across ${metricRows} trend-ready flight${metricRows === 1 ? "" : "s"}.`);
    } else {
      pieces.push("No repeated review cue is present in this trend area.");
    }
  } else if (selectedFlagged) {
    const fullSet = selectedCount ? `selected ${selectedCount}-flight trend set` : "selected trend set";
    pieces.push(`Across the ${fullSet}, ${selectedFlagged} flagged ${label}.`);
  } else if (Number.isFinite(latestScore) && latestScore < 88) {
    pieces.push("The latest trend row is below the stable band.");
  } else {
    pieces.push("The deterministic trend model marked this area for follow-up.");
  }
  if (affected) pieces.push(`Focus: ${affected}.`);
  if (signal?.direction) pieces.push(`Direction is ${deterministicSignalDirectionText(signal)}.`);
  return pieces.join(" ");
}

function deterministicSignalItem(signal, tone) {
  const status = deterministicSignalStatus(signal);
  const label = compactText(signal?.label ?? signal?.title, "Trend");
  return {
    label,
    value: tone === "positive"
      ? (signal?.recovered ? "Improved" : "Stable signal")
      : (status?.className === "red" ? "Needs attention" : status?.label || "Watch item"),
    detail: deterministicSignalDetail(signal, tone),
    tone,
  };
}

function deterministicWatchSignalSort(a, b) {
  const aStatus = deterministicSignalStatus(a);
  const bStatus = deterministicSignalStatus(b);
  const aSeverity = aStatus?.className === "red" ? 0 : 1;
  const bSeverity = bStatus?.className === "red" ? 0 : 1;
  return aSeverity - bSeverity
    || Number(b?.sortWeight ?? 0) - Number(a?.sortWeight ?? 0)
    || Number(a?.latestScore ?? 100) - Number(b?.latestScore ?? 100)
    || String(a?.label ?? "").localeCompare(String(b?.label ?? ""));
}

function deterministicGoodSignalSort(a, b) {
  return deterministicSignalRank(a) - deterministicSignalRank(b)
    || Number(b?.latestScore ?? 0) - Number(a?.latestScore ?? 0)
    || String(a?.label ?? "").localeCompare(String(b?.label ?? ""));
}

function deterministicFocusItems(watchSignals) {
  const focusSignals = watchSignals.slice(0, 2);
  if (!focusSignals.length) {
    return [{
      label: "Maintain trend baseline",
      value: "Next comparison",
      detail: "Keep adding normal, representative flights so future changes have a clean baseline.",
      tone: "focus",
    }];
  }
  return focusSignals.map((signal) => ({
    label: `Compare ${compactText(signal?.label, "trend area")}`,
    value: "Next comparison",
    detail: compactText(signal?.action, "Compare future similar flights against the same phase, power, and operating context."),
    tone: "focus",
  }));
}

function deterministicTrendBriefingModel(signals) {
  const usableSignals = (Array.isArray(signals) ? signals : []).filter(Boolean);
  const goodSignals = usableSignals
    .filter((signal) => deterministicSignalStatus(signal)?.className === "green")
    .sort(deterministicGoodSignalSort)
    .slice(0, 4);
  const watchSignals = usableSignals
    .filter((signal) => {
      const status = deterministicSignalStatus(signal);
      return status?.className === "amber" || status?.className === "red";
    })
    .sort(deterministicWatchSignalSort)
    .slice(0, 4);
  return {
    goodSigns: goodSignals.map((signal) => deterministicSignalItem(signal, "positive")),
    needsAttention: watchSignals.map((signal) => deterministicSignalItem(signal, "watch")),
    nextComparisons: deterministicFocusItems(watchSignals),
  };
}

function positiveTopicItems(summary) {
  const text = compactText([
    summary?.headline,
    summary?.plainLanguageOverview,
    summary?.coverageAssessment,
    ...(Array.isArray(summary?.supportingEvidence) ? summary.supportingEvidence.flatMap((item) => [item?.label, item?.value, item?.detail]) : []),
    ...(Array.isArray(summary?.mostImportantChanges) ? summary.mostImportantChanges.flatMap((item) => [item?.title, item?.text]) : []),
  ].filter(Boolean).join(" "));
  const topics = [
    {
      label: "Clean EGT Rhythm",
      match: /\bEGT Rhythm\b.{0,90}\b(clean|stable|steady|healthy|good)\b|\b(clean|stable|steady|healthy|good)\b.{0,90}\bEGT Rhythm\b/i,
      detail: "EGT Rhythm is part of the trend-sensitive review and reads clean in this selection.",
    },
    {
      label: "Oil pressure stability",
      match: /\boil\b.{0,90}\b(stable|stability|steady|healthy|good)\b|\b(stable|stability|steady|healthy|good)\b.{0,90}\boil\b/i,
      detail: "Oil pressure and temperature context read as stable in the selected trend summary.",
    },
  ];
  return topics
    .filter((topic) => topic.match.test(text))
    .map((topic) => ({
      label: topic.label,
      value: "Stable signal",
      detail: topic.detail,
      tone: "positive",
    }));
}

function itemMatchesTopic(item, topic) {
  const label = compactText(item?.label).toLowerCase();
  const topicLabel = compactText(topic?.label).toLowerCase();
  if (!label || !topicLabel) return false;
  if (label === topicLabel) return true;
  if (topicLabel.includes("oil") && label.includes("oil")) return true;
  if (topicLabel.includes("egt rhythm") && label.includes("egt rhythm")) return true;
  return false;
}

function prioritizeVisibleTopics(items, topicItems) {
  const remaining = uniqueItems(items);
  const promoted = [];
  for (const topic of topicItems) {
    const index = remaining.findIndex((item) => itemMatchesTopic(item, topic));
    if (index < 0) continue;
    promoted.push(remaining.splice(index, 1)[0]);
  }
  return uniqueItems([...promoted, ...remaining]);
}

function ensurePositiveTopicCoverage(items, topicItems, limit) {
  const result = uniqueItems(items).slice(0, limit);
  for (const topic of topicItems) {
    if (result.some((item) => itemMatchesTopic(item, topic))) continue;
    if (result.length < limit) {
      result.push(topic);
    } else {
      result[result.length - 1] = topic;
    }
  }
  return prioritizeVisibleTopics(result, topicItems).slice(0, limit);
}

function briefingDisplayValue(item) {
  const value = compactText(item?.value);
  if (!value) return "";
  if (/^(steady|improving|worsening|healthy stable review area|stable signal|watch item|needs trend attention)$/i.test(value)) return "";
  return value;
}

function briefingLaneValue(item) {
  const value = compactText(item?.value);
  if (!value) return "";
  if (/^healthy stable review area$/i.test(value)) return "Stable review";
  if (/^stable signal$/i.test(value)) return "Stable signal";
  if (/^watch item$/i.test(value)) return "Watch item";
  if (/^needs trend attention$/i.test(value)) return "Needs attention";
  if (/^steady$/i.test(value)) return "Steady";
  if (/^improving$/i.test(value)) return "Improving";
  if (/^worsening$/i.test(value)) return "Worsening";
  return value;
}

const trendVisualLabels = Object.freeze({
  chtHeatProfile: "CHT Heat Profile",
  chtDistribution: "CHT Distribution Lanes",
  egtBalance: "EGT Balance",
  turboMargin: "Turbo Margin",
  inductionLeak: "Induction Leak Cues",
  coolingMargin: "Highest CHT by Flight",
  chtCoolingRate: "CHT Cooling Rate",
  oilPressure: "Oil Pressure by Flight",
  oilEnvelope: "Oil Pressure vs Oil Temperature",
  egtRhythm: "EGT Rhythm Signature",
  electricalVoltage: "Electrical",
  sensorQuality: "Sensor Quality Trust Trend",
  gamiSweep: "GAMI Sweep Spread",
});

function briefingItemVisualKey(item) {
  const text = compactText([item?.label, item?.value, item?.detail].filter(Boolean).join(" ")).toLowerCase();
  if (!text) return "";
  if (/\bgami\b|injector|sweep/.test(text)) return "gamiSweep";
  if (/sensor|probe|trust|quality|flatline|missing/.test(text)) return "sensorQuality";
  if (/electrical|bus|voltage|charging|alternator/.test(text)) return "electricalVoltage";
  if (/egt\s*rhythm|valve|cyclical/.test(text)) return "egtRhythm";
  if (/\begt\b|exhaust/.test(text) && /balance|spread/.test(text)) return "egtBalance";
  if (/turbo|boost|\btit\b|\bmap\b|manifold|upper deck/.test(text)) return "turboMargin";
  if (/induction|intake leak|fuel[-\s]?flow drop/.test(text)) return "inductionLeak";
  if (/\boil\b/.test(text)) {
    if (/temp|temperature|warm|warms|warming|envelope|pressure\s*\/\s*temperature/.test(text)) return "oilEnvelope";
    return "oilPressure";
  }
  if (/cooling\s+rate|cooldown|cool down|fast cooling|rapid cooling|descent|power[-\s]?reduction/.test(text)) return "chtCoolingRate";
  if (/\bcht\b|cylinder head|cooling/.test(text)) {
    if (/cooling|rate|descent|power[-\s]?reduction/.test(text)) return "chtCoolingRate";
    if (/highest|hottest|peak|max|high-end|margin/.test(text)) return "coolingMargin";
    if (/distribution|lane|usually runs/.test(text)) return "chtDistribution";
    if (/heat|thermal|temperature|warm|\bcht\b/.test(text)) return "chtHeatProfile";
  }
  return "";
}

function trendVisualLinkLabel(visualKey) {
  return trendVisualLabels[visualKey] ?? "trend";
}

export function trendAiSummaryOwnerBriefingModel(summary, { flightCount = 0, deterministicSignals = null } = {}) {
  const evidence = evidenceItems(summary, 8);
  const changes = changeItems(summary);
  const positiveTopics = positiveTopicItems(summary);
  const deterministicBriefing = Array.isArray(deterministicSignals)
    ? deterministicTrendBriefingModel(deterministicSignals)
    : null;
  const goodSigns = deterministicBriefing?.goodSigns ?? ensurePositiveTopicCoverage([
    ...evidence.filter((item) => item.tone === "positive"),
    ...positiveTopics,
    ...changes.filter((item) => item.tone === "positive"),
  ], positiveTopics, 4);
  const needsAttention = deterministicBriefing?.needsAttention ?? prioritizeVisibleTopics(uniqueItems([
    ...changes.filter((item) => item.tone === "watch"),
    ...evidence.filter((item) => item.tone === "watch"),
  ]), [{ label: "Oil Pressure / Temperature" }]).slice(0, 4);
  const nextComparisons = deterministicBriefing?.nextComparisons ?? focusItems(summary);
  const limitation = (Array.isArray(summary?.dataLimitations) ? summary.dataLimitations : [])
    .map((item) => compactSentence(item?.text ?? item?.title, "", 170))
    .find(Boolean);
  const sourceFlightCount = summary?.source?.flightCount ?? flightCount;
  return {
    goodSigns: goodSigns.length ? goodSigns : evidence.slice(0, 2),
    needsAttention,
    nextComparisons: nextComparisons.length ? nextComparisons : [{
      label: "Compare future similar flights",
      detail: "Use the same operating conditions where possible and watch whether the highlighted trend repeats.",
      tone: "focus",
    }],
    limitation: limitation || "This is evidence translation, not a diagnosis or an airworthiness decision.",
    verdict: summaryVerdict(summary, deterministicSignals),
    confidence: confidenceLabel(summary),
    reviewedCount: sourceFlightCount,
  };
}

function briefingItemHtml(item, { detailMax = 118, valueMax = 72 } = {}) {
  const detail = detailMax > 0 ? compactSentence(item.detail || item.value, "", detailMax) : "";
  const value = briefingDisplayValue(item);
  const visualKey = briefingItemVisualKey(item);
  const body = `
    <span class="trend-ai-summary-briefing-dot" aria-hidden="true"></span>
    <span class="trend-ai-summary-briefing-copy">
      <span class="trend-ai-summary-briefing-label">${escapeHtml(item.label)}</span>
      ${value ? `<span class="trend-ai-summary-briefing-value">${escapeHtml(compactSentence(value, "", valueMax))}</span>` : ""}
      ${detail ? `<span class="trend-ai-summary-briefing-detail">${escapeHtml(detail)}</span>` : ""}
    </span>
  `;
  if (visualKey) {
    return `
      <li class="trend-ai-summary-briefing-item is-${escapeHtml(item.tone)} is-linked">
        <button class="trend-ai-summary-briefing-link" type="button" data-trend-visual="${escapeHtml(visualKey)}" aria-label="${escapeHtml(`Focus ${trendVisualLinkLabel(visualKey)} trend card`)}">
          ${body}
        </button>
      </li>
    `;
  }
  return `
    <li class="trend-ai-summary-briefing-item is-${escapeHtml(item.tone)}">
      ${body}
    </li>
  `;
}

function briefingLaneItemHtml(item) {
  const value = briefingLaneValue(item);
  const visualKey = briefingItemVisualKey(item);
  const body = `
    <span class="trend-ai-summary-briefing-dot" aria-hidden="true"></span>
    <span class="trend-ai-summary-briefing-copy">
      <span class="trend-ai-summary-briefing-label">${escapeHtml(item.label)}</span>
      ${value ? `<span class="trend-ai-summary-briefing-value">${escapeHtml(value)}</span>` : ""}
    </span>
  `;
  if (visualKey) {
    return `
      <li class="trend-ai-summary-briefing-item is-${escapeHtml(item.tone)} is-linked">
        <button class="trend-ai-summary-briefing-link" type="button" data-trend-visual="${escapeHtml(visualKey)}" aria-label="${escapeHtml(`Focus ${trendVisualLinkLabel(visualKey)} trend card`)}">
          ${body}
        </button>
      </li>
    `;
  }
  return `
    <li class="trend-ai-summary-briefing-item is-${escapeHtml(item.tone)}">
      ${body}
    </li>
  `;
}

function visibleBriefingItems(items) {
  return (Array.isArray(items) ? items : []).slice(0, 4);
}

function briefingDetailItemHtml(item) {
  const value = briefingLaneValue(item);
  const detail = compactText(item?.detail || item?.value);
  const visualKey = briefingItemVisualKey(item);
  const body = `
    <span class="trend-ai-summary-briefing-dot" aria-hidden="true"></span>
    <span class="trend-ai-summary-detail-copy">
      <span class="trend-ai-summary-detail-label">${escapeHtml(item.label)}</span>
      ${value ? `<span class="trend-ai-summary-detail-value">${escapeHtml(value)}</span>` : ""}
      ${detail ? `<span class="trend-ai-summary-detail-text">${escapeHtml(detail)}</span>` : ""}
    </span>
  `;
  if (visualKey) {
    return `
      <li class="trend-ai-summary-detail-item is-${escapeHtml(item.tone)} is-linked">
        <button class="trend-ai-summary-detail-link" type="button" data-trend-visual="${escapeHtml(visualKey)}" aria-label="${escapeHtml(`Focus ${trendVisualLinkLabel(visualKey)} trend card`)}">
          ${body}
        </button>
      </li>
    `;
  }
  return `
    <li class="trend-ai-summary-detail-item is-${escapeHtml(item.tone)}">
      ${body}
    </li>
  `;
}

function briefingLaneDetailHtml({ tone, items }) {
  const visibleItems = visibleBriefingItems(items);
  if (!visibleItems.length) return "";
  return `
    <details class="trend-ai-summary-lane-more is-${escapeHtml(tone)}">
      <summary>More detail</summary>
      <ul>${visibleItems.map(briefingDetailItemHtml).join("")}</ul>
    </details>
  `;
}

function briefingGroupHtml({ title, tone, items, emptyText }) {
  const list = items.length
    ? items.map(briefingItemHtml).join("")
    : `<li class="trend-ai-summary-briefing-empty">${escapeHtml(emptyText)}</li>`;
  return `
    <section class="trend-ai-summary-briefing-section is-${escapeHtml(tone)}">
      <h3>${escapeHtml(title)}</h3>
      <ul>${list}</ul>
    </section>
  `;
}

function aiSummaryMarkHtml() {
  return `
    <span class="trend-ai-summary-mark" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        <path d="M6 13.5c2.8-.1 3.1-4.9 6-4.9s3.2 4.8 6 4.9" />
        <path d="M6 17c2.4-.1 3.5-2.4 6-2.4s3.6 2.3 6 2.4" />
        <path d="M12 5.2v3.3" />
        <path d="M8.4 6.6l1.8 2.3" />
        <path d="M15.6 6.6l-1.8 2.3" />
      </svg>
    </span>
  `;
}

function briefingLaneIconHtml(tone) {
  if (tone === "positive") {
    return `
      <span class="trend-ai-summary-lane-symbol" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M4 16.5l5.2-5.2 3.7 3.7 6.8-7" />
          <path d="M15.8 8h3.9v3.9" />
        </svg>
      </span>
    `;
  }
  if (tone === "watch") {
    return `
      <span class="trend-ai-summary-lane-symbol" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M12 3.8l7 3v5.4c0 4.3-2.7 6.8-7 8-4.3-1.2-7-3.7-7-8v-5.4l7-3z" />
          <path d="M12 8.2v5.2" />
          <path d="M12 16.7h.01" />
        </svg>
      </span>
    `;
  }
  return `
    <span class="trend-ai-summary-lane-symbol" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        <circle cx="10.5" cy="10.5" r="5.7" />
        <path d="M15 15l5 5" />
        <path d="M10.5 7.5v3.3l2.4 1.4" />
      </svg>
    </span>
  `;
}

function briefingLaneHtml({ title, tone, countLabel, items, emptyText }) {
  const visibleItems = visibleBriefingItems(items);
  const list = visibleItems.length
    ? visibleItems.map(briefingLaneItemHtml).join("")
    : `<li class="trend-ai-summary-briefing-empty">${escapeHtml(emptyText)}</li>`;
  return `
    <section class="trend-ai-summary-briefing-section is-${escapeHtml(tone)}">
      <div class="trend-ai-summary-lane-heading">
        <span class="trend-ai-summary-lane-title">
          <span class="trend-ai-summary-lane-dot" aria-hidden="true"></span>
          <h3>${escapeHtml(title)}</h3>
        </span>
        ${briefingLaneIconHtml(tone)}
      </div>
      <div class="trend-ai-summary-lane-count">
        <strong>${escapeHtml(String(items.length))}</strong>
        <span>${escapeHtml(countLabel)}</span>
      </div>
      <ul>${list}</ul>
      ${briefingLaneDetailHtml({ tone, items })}
    </section>
  `;
}

function summaryVerdict(summary, deterministicSignals = null) {
  if (Array.isArray(deterministicSignals)) {
    const statuses = deterministicSignals.map(deterministicSignalStatus).filter(Boolean);
    if (statuses.some((status) => status.className === "red")) return { label: "Attention", className: "red" };
    if (statuses.some((status) => status.className === "amber")) return { label: "Watch", className: "amber" };
    if (statuses.some((status) => status.className === "green")) return { label: "Stable", className: "green" };
  }
  // Text-only fallback (no deterministic signals available): model prose alone can
  // never earn red — the deterministic ladder is the only source of Attention.
  const changes = changeItems(summary);
  const evidence = evidenceItems(summary, 6);
  const text = compactText(`${summary?.headline ?? ""} ${summary?.plainLanguageOverview ?? ""}`);
  const hasWorsening = changes.some((item) => item.tone === "watch" && /worsening|worse/i.test(item.value))
    || /investigate|attention is needed|worsening|rapid cooling|flagged recently/i.test(text);
  if (hasWorsening
    || changes.some((item) => item.tone === "watch")
    || evidence.some((item) => item.tone === "watch")
    || /\bwatch status\b|needs attention|should be reviewed|deserves prominent trend attention/i.test(text)) {
    return { label: "Watch", className: "amber" };
  }
  return { label: "Stable", className: "green" };
}

function confidenceLabel(summary) {
  const value = compactText(summary?.confidence);
  if (!value) return "";
  return `Confidence: ${value[0].toUpperCase()}${value.slice(1)}`;
}

function naturalList(items, limit = 3) {
  const labels = (Array.isArray(items) ? items : [])
    .map((item) => compactText(item?.label ?? item))
    .filter(Boolean)
    .slice(0, limit);
  if (!labels.length) return "";
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels.at(-1)}`;
}

function sentenceCaseFragment(value) {
  const text = compactText(value);
  if (!text) return "";
  return `${text[0].toLowerCase()}${text.slice(1)}`;
}

function deterministicSummaryAvailable(deterministicSignals) {
  return Array.isArray(deterministicSignals) && deterministicSignals.some(Boolean);
}

function deterministicStableItems(deterministicSignals) {
  return (Array.isArray(deterministicSignals) ? deterministicSignals : [])
    .filter((signal) => deterministicSignalStatus(signal)?.className === "green")
    .sort(deterministicGoodSignalSort)
    .map((signal) => deterministicSignalItem(signal, "positive"));
}

function groundedTrendHeadline(summary, briefing, deterministicSignals) {
  const fallback = compactSentence(summary?.headline, "Trend summary ready.", 210);
  if (!deterministicSummaryAvailable(deterministicSignals) || !briefing) return fallback;
  const watchList = naturalList(briefing.needsAttention, 2);
  if (watchList) {
    return `Overall, your engine's trend history looks stable with ${watchList} to monitor closely.`;
  }
  return "Overall, your engine's trend history looks stable across the selected flights.";
}

function groundedTrendOverview(summary, briefing, deterministicSignals, fallbackOverview) {
  if (!deterministicSummaryAvailable(deterministicSignals) || !briefing) return fallbackOverview;
  const reviewedCount = Number(briefing.reviewedCount ?? summary?.source?.flightCount ?? 0);
  const flightText = reviewedCount > 0
    ? `${reviewedCount} flight${reviewedCount === 1 ? "" : "s"}`
    : "selected flights";
  const goodList = naturalList(briefing.goodSigns, 4);
  const stableBreadth = naturalList(deterministicStableItems(deterministicSignals), 6) || goodList;
  const watchList = naturalList(briefing.needsAttention, 3);
  const focus = briefing.nextComparisons?.[0] ?? null;
  const focusDetail = compactText(focus?.detail);
  const watchDetails = (briefing.needsAttention ?? [])
    .map((item) => compactSentence(item?.detail, "", 150))
    .filter(Boolean)
    .slice(0, 2)
    .join(" ");

  const broadRead = watchList
    ? `Across the selected ${flightText}, the deterministic trend evidence shows ${stableBreadth || "several review areas"} in stable shape, with ${watchList} as the area${briefing.needsAttention.length === 1 ? "" : "s"} to compare next.`
    : `Across the selected ${flightText}, the deterministic trend evidence shows ${stableBreadth || "the reviewed areas"} in stable shape, and no repeated watch item stands out in this selection.`;
  const stableRead = goodList
    ? `The Good lane is led by ${goodList}. These are stable signals in this trend set, not hidden watch cues.`
    : "The stable areas remain useful positive evidence in this trend set.";
  const watchRead = watchList
    ? `${watchList} ${briefing.needsAttention.length === 1 ? "is" : "are"} the deterministic watch focus. ${watchDetails}`.trim()
    : stableRead;
  const nextRead = focusDetail
    ? `For the next comparison, ${sentenceCaseFragment(focusDetail)} This summary translates deterministic trend evidence; it is not a diagnosis or an airworthiness decision.`
    : "For the next comparison, keep adding representative flights under similar operating conditions. This summary translates deterministic trend evidence; it is not a diagnosis or an airworthiness decision.";

  return [broadRead, watchRead, nextRead].join("\n\n");
}

function actionButton({ action, label, disabled = false } = {}) {
  if (!action) return "";
  return `
    <button class="secondary-action trend-ai-summary-action" data-trend-ai-summary-action="${escapeHtml(action)}" type="button"${disabled ? " disabled" : ""}>
      ${escapeHtml(label)}
    </button>
  `;
}

function trendAiSummaryPlanLimitInfo(state) {
  const info = state?.limitInfo;
  const limit = Math.max(0, Math.floor(Number(info?.limit ?? 0)));
  const current = Math.max(0, Math.floor(Number(info?.current ?? 0)));
  if (!limit || current < limit) return null;
  const plan = String(info?.plan ?? "").trim().toLowerCase();
  const planLabel = plan ? `${plan.slice(0, 1).toUpperCase()}${plan.slice(1)}` : "current";
  return {
    current,
    limit,
    planLabel,
    overLimit: current > limit,
    progress: Math.min(100, Math.round((current / limit) * 100)),
    packPurchasesEnabled: info?.packPurchasesEnabled === true,
    purchaseUrl: info?.purchaseUrl ?? "/#account/plan",
  };
}

function trendAiSummaryLimitNoticeMarkup(limitInfo, flightCount) {
  if (!limitInfo) return "";
  const {
    current,
    limit,
    planLabel,
    overLimit,
    progress,
    packPurchasesEnabled,
    purchaseUrl,
  } = limitInfo;
  const flightLabel = `${flightCount} included flight${flightCount === 1 ? "" : "s"}`;
  const usageMessage = overLimit
    ? `You've used ${current} AI requests this month. The ${planLabel} plan includes ${limit} monthly AI requests.`
    : `The ${planLabel} plan includes ${limit} monthly AI requests, and all ${current} have been used.`;
  const usageLabel = overLimit ? "used this month" : `of ${limit} used`;
  const meterValue = Math.min(current, limit);
  const meterText = overLimit
    ? `${current} AI requests used this month; ${limit} included with the ${planLabel} plan`
    : `${current} of ${limit} monthly AI requests used`;
  return `
    <div class="ai-summary-limit-notice trend-ai-summary-limit-notice" role="status" aria-labelledby="trendAiSummaryLimitTitle">
      <span class="ai-summary-limit-icon" aria-hidden="true">
        <svg viewBox="0 0 48 48" focusable="false">
          <path d="M12 32a12 12 0 0 1 24 0" />
          <path d="M24 19v12" />
          <circle cx="24" cy="33" r="2.5" />
        </svg>
      </span>
      <div class="ai-summary-limit-copy">
        <p class="ai-summary-limit-eyebrow">AI allowance <span>${escapeHtml(flightLabel)}</span></p>
        <h2 id="trendAiSummaryLimitTitle">AI trend summary limit reached</h2>
        <p>${escapeHtml(usageMessage)} Flight summaries, trend summaries, and Graph Ask share this allowance.</p>
        <span class="ai-summary-limit-renewal">Your next allowance begins at the start of next month.</span>
        ${packPurchasesEnabled ? `<a class="secondary-action ai-summary-limit-purchase" href="${escapeHtml(purchaseUrl)}">Buy more AI requests</a>` : ""}
      </div>
      <div class="ai-summary-limit-usage">
        <strong>${escapeHtml(current)} <span>${escapeHtml(usageLabel)}</span></strong>
        <div class="ai-summary-limit-meter" role="progressbar" aria-label="Monthly AI requests used" aria-valuemin="0" aria-valuemax="${escapeHtml(limit)}" aria-valuenow="${escapeHtml(meterValue)}" aria-valuetext="${escapeHtml(meterText)}">
          <span style="width: ${escapeHtml(progress)}%"></span>
        </div>
        <span class="ai-summary-limit-caption">Monthly requests</span>
      </div>
    </div>
  `;
}

function toggleButton({ collapsed = false } = {}) {
  const action = collapsed ? "show" : "hide";
  const label = collapsed ? "Show AI Summary" : "Hide AI Summary";
  return `
    <button class="secondary-action trend-ai-summary-toggle" data-trend-ai-summary-toggle="${action}" type="button" aria-expanded="${collapsed ? "false" : "true"}" aria-controls="trendAiSummaryCard">
      ${escapeHtml(label)}
    </button>
  `;
}

export function trendAiSummaryPanelMarkup({
  state = { status: "idle", payload: null, error: "" },
  flightCount = 0,
  minFlights = 3,
  canGenerate = false,
  readOnly = false,
  collapsed = false,
  deterministicSignals = null,
} = {}) {
  const status = state?.status ?? "idle";
  const summary = trendAiSummaryPayloadJson(state?.payload);
  const generatedLabel = trendAiSummaryGeneratedLabel(state?.payload);
  const unavailable = status === "unavailable" || flightCount < minFlights || !canGenerate;
  const generating = status === "loading";
  const checking = status === "checking";
  const loading = generating || checking;
  const ready = status === "ready" && summary;
  const readyCollapsed = ready && collapsed;
  const error = status === "error";
  const limitInfo = trendAiSummaryPlanLimitInfo(state);
  const limitReached = Boolean(limitInfo);
  const action = ready ? "regenerate" : unavailable || loading || limitReached ? "" : "generate";
  const actionLabel = ready ? "Regenerate" : "Generate";
  const meta = ready && generatedLabel
    ? `Generated ${generatedLabel}`
    : loading
      ? (generating ? "Generating" : "Checking")
      : `${flightCount} included flight${flightCount === 1 ? "" : "s"}`;
  const evidence = ready ? evidenceItems(summary) : [];
  const briefing = ready ? trendAiSummaryOwnerBriefingModel(summary, { flightCount, deterministicSignals }) : null;
  const rawOverview = ready ? (summary.plainLanguageOverview ?? summary.coverageAssessment ?? "") : "";
  const title = ready
    ? groundedTrendHeadline(summary, briefing, deterministicSignals)
    : error
      ? "AI trend summary unavailable"
      : unavailable
        ? `AI trend summary needs ${minFlights} trend-ready flights`
        : "AI trend summary";
  const overview = ready
    ? groundedTrendOverview(summary, briefing, deterministicSignals, rawOverview)
    : error
      ? compactText(state?.error, "Could not load the AI trend summary.")
      : loading
        ? "Checking the latest saved trend summary."
        : readOnly
          ? "Saved AI trend summary not available for this aircraft history."
          : "Ready to summarize the included trend flights.";
  const showBoundary = ready ? shouldShowBoundary(overview, briefing.limitation) : false;

  return `
    <section class="trend-ai-summary-card status-${escapeHtml(ready ? "ready" : limitReached ? "limit" : error ? "error" : loading ? "loading" : unavailable ? "unavailable" : "idle")}${ready ? ` tone-${escapeHtml(briefing.verdict.className)}` : ""}${readyCollapsed ? " is-collapsed" : ""}" id="trendAiSummaryCard" aria-live="polite">
      ${readyCollapsed ? `
        <div class="trend-ai-summary-collapsed">
          <div class="trend-ai-summary-header">
            <div class="trend-ai-summary-title-row">
              ${aiSummaryMarkHtml()}
              <span class="trend-ai-summary-title-copy">
                <span class="trend-ai-summary-kicker">AI trend summary</span>
                <span class="trend-ai-summary-meta">${escapeHtml(meta)}</span>
              </span>
            </div>
            <div class="trend-ai-summary-header-actions">
              ${toggleButton({ collapsed: true })}
            </div>
          </div>
          <p class="trend-ai-summary-headline">${escapeHtml(title)}</p>
        </div>
      ` : ready ? `
        <div class="trend-ai-summary-command-strip">
          <div class="trend-ai-summary-lead">
            <div class="trend-ai-summary-header">
              <div class="trend-ai-summary-title-row">
                ${aiSummaryMarkHtml()}
                <span class="trend-ai-summary-title-copy">
                  <span class="trend-ai-summary-kicker">AI trend summary</span>
                  <span class="trend-ai-summary-meta">${escapeHtml(meta)}</span>
                </span>
              </div>
              <div class="trend-ai-summary-header-actions">
                ${toggleButton({ collapsed: false })}
                ${actionButton({ action, label: actionLabel, disabled: readOnly })}
              </div>
            </div>
            <div class="trend-ai-summary-status-strip">
              <span>${escapeHtml(briefing.reviewedCount)} flight${briefing.reviewedCount === 1 ? "" : "s"} reviewed</span>
            </div>
            <p class="trend-ai-summary-headline">${escapeHtml(title)}</p>
            ${overviewParagraphsHtml(overview)}
            ${showBoundary ? `<p class="trend-ai-summary-boundary">${escapeHtml(briefing.limitation)}</p>` : ""}
          </div>
          <div class="trend-ai-summary-briefing-grid">
            ${briefingLaneHtml({
              title: "Good",
              tone: "positive",
              countLabel: `signal${briefing.goodSigns.length === 1 ? "" : "s"}`,
              items: briefing.goodSigns,
              emptyText: "No strong positive signal was available in this summary.",
            })}
            ${briefingLaneHtml({
              title: "Watch",
              tone: "watch",
              countLabel: `signal${briefing.needsAttention.length === 1 ? "" : "s"}`,
              items: briefing.needsAttention,
              emptyText: "No repeated watch item stands out in this selection.",
            })}
            ${briefingLaneHtml({
              title: "Next check",
              tone: "focus",
              countLabel: `action${briefing.nextComparisons.length === 1 ? "" : "s"}`,
              items: briefing.nextComparisons,
              emptyText: "Compare future similar flights against this trend set.",
            })}
          </div>
        </div>
      ` : limitReached ? `
        ${trendAiSummaryLimitNoticeMarkup(limitInfo, flightCount)}
      ` : generating ? `
        ${trendAiSummaryLoadingMarkup()}
      ` : `
        <div class="trend-ai-summary-header">
          <div>
            <span class="trend-ai-summary-kicker">AI trend summary</span>
            <span class="trend-ai-summary-meta">${escapeHtml(meta)}</span>
          </div>
          ${actionButton({ action, label: actionLabel, disabled: readOnly })}
        </div>
        <p class="trend-ai-summary-headline">${escapeHtml(title)}</p>
        <p class="trend-ai-summary-overview">${escapeHtml(overview)}</p>
        ${evidence.length ? `<div class="trend-ai-summary-evidence-grid">${evidence.map((item) => briefingGroupHtml({
          title: item.label,
          tone: item.tone,
          items: [item],
          emptyText: "",
        })).join("")}</div>` : ""}
      `}
    </section>
  `;
}

export function trendAiSummaryErrorMessage(error) {
  const message = openAiUserFacingErrorMessage(error?.message ?? error ?? "")
    .replace(/^Could not build trend AI summary:\s*/i, "")
    .trim();
  if (!message) return "Could not load the AI trend summary.";
  if (/OPENAI_API_KEY|model is not configured|rejected the configured API key/i.test(message)) {
    return "AI trend summaries are not available right now. If this keeps happening, the server's AI provider needs a valid OPENAI_API_KEY, or AEROINSIGHT_TREND_AI_SUMMARY_FAKE=1 for local testing.";
  }
  if (/requires at least/i.test(message)) return message;
  return message;
}
